import React, { useEffect, useMemo, useState } from 'react';
import { Check, Layers, Pencil, Plus, RefreshCw, Search, Trash2, Users, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { workspaceAdminAPI } from '../../services/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { BASE_PLAN_CURRENCY, PLAN_CURRENCY_OPTIONS, convertPlanPriceFromBase, convertPlanPriceToBase, formatPlanPriceFromBase } from '../../lib/currency';
import { getAiDashboardAddonTotals } from '../../lib/aiDashboardAddons';
import { getErrorMessage } from '../../lib/utils';
import { useTableControls } from '../../hooks/useTableControls';
import { TableControls } from '../ui/table-controls';
import { StatCard } from '../common/StatCard';
import { CardSkeleton, EmptyState } from '../common/States';

const buildEmptyRolePermissions = (roles = []) =>
  (roles || []).reduce((acc, role) => ({ ...acc, [role]: [] }), {});

const SubscriptionPlansRBAC = ({ onCreatePlan, onEditPlan }) => {
  const [catalog, setCatalog] = useState({ modules: [], permissions: [], roles: [] });
  const [plans, setPlans] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bootstrapLoading, setBootstrapLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(BASE_PLAN_CURRENCY);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignment, setAssignment] = useState({ tenantId: '', planId: '' });
  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    monthly_price: '0',
    yearly_price: '0',
    is_active: true,
    module_access: [],
    role_permissions: {},
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [catalogRes, plansRes, tenantsRes] = await Promise.all([
        workspaceAdminAPI.getSubscriptionCatalog(),
        workspaceAdminAPI.listSubscriptionPlans({ include_inactive: true }),
        workspaceAdminAPI.listTenants(),
      ]);
      setCatalog(catalogRes.data || { modules: [], permissions: [], roles: [] });
      const roleDefaults = buildEmptyRolePermissions(catalogRes.data?.roles || []);
      const plansPayload = plansRes.data;
      const plansList = Array.isArray(plansPayload)
        ? plansPayload
        : Array.isArray(plansPayload?.items)
          ? plansPayload.items
          : [];
      setPlans(plansList);
      setTenants(Array.isArray(tenantsRes.data) ? tenantsRes.data : []);
      setForm((prev) => ({ ...prev, role_permissions: Object.keys(prev.role_permissions || {}).length > 0 ? prev.role_permissions : roleDefaults }));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load subscription plan data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const selectedPlan = useMemo(
    () => plans.find((row) => String(row.id) === String(selectedPlanId)),
    [plans, selectedPlanId]
  );

  const tenantCurrentPlanById = useMemo(() => {
    return new Map(
      (tenants || []).map((tenant) => [
        String(tenant.id),
        tenant.subscription_plan_name || tenant.subscription_plan_code || 'Unassigned',
      ])
    );
  }, [tenants]);

  const filteredPlans = useMemo(() => {
    return (plans || []).filter((plan) => {
      const matchesSearch = `${plan.name || ''} ${plan.code || ''} ${plan.description || ''}`
        .toLowerCase()
        .includes(searchTerm.trim().toLowerCase());
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && plan.is_active) ||
        (statusFilter === 'inactive' && !plan.is_active);
      return matchesSearch && matchesStatus;
    });
  }, [plans, searchTerm, statusFilter]);

  const planTable = useTableControls(filteredPlans, {
    initialSortKey: 'name',
    initialSortDirection: 'asc',
  });

  const activePlansCount = useMemo(
    () => (plans || []).filter((plan) => plan.is_active).length,
    [plans]
  );

  const assignedTenantsCount = useMemo(
    () => (tenants || []).filter((tenant) => tenant.subscription_plan_id).length,
    [tenants]
  );
  const rolePermissionCoverage = useMemo(() => {
    const rows = {};
    plans.forEach((plan) => {
      const map = plan.role_permissions || {};
      rows[plan.id] = Object.keys(map).reduce((acc, role) => {
        acc[role] = Array.isArray(map[role]) ? map[role].length : 0;
        return acc;
      }, {});
    });
    return rows;
  }, [plans]);

  useEffect(() => {
    if (!selectedPlan) {
      return;
    }
    setForm({
      code: selectedPlan.code || '',
      name: selectedPlan.name || '',
      description: selectedPlan.description || '',
      monthly_price: String(selectedPlan.monthly_price || 0),
      yearly_price: String(selectedPlan.yearly_price || 0),
      is_active: Boolean(selectedPlan.is_active),
      module_access: selectedPlan.module_access || [],
      role_permissions: selectedPlan.role_permissions || buildEmptyRolePermissions(catalog.roles),
    });
  }, [selectedPlanId, selectedPlan, catalog.roles]);

  const resetForm = () => {
    setSelectedPlanId(null);
    setForm({
      code: '',
      name: '',
      description: '',
      monthly_price: '0',
      yearly_price: '0',
      is_active: true,
      module_access: [],
      role_permissions: buildEmptyRolePermissions(catalog.roles),
    });
  };

  const toggleModule = (moduleKey, checked) => {
    setForm((prev) => ({
      ...prev,
      module_access: checked ? [...prev.module_access, moduleKey] : prev.module_access.filter((row) => row !== moduleKey),
    }));
  };

  const togglePermission = (role, permission, checked) => {
    setForm((prev) => {
      const existing = Array.isArray(prev.role_permissions?.[role]) ? prev.role_permissions[role] : [];
      const next = checked ? [...existing, permission] : existing.filter((row) => row !== permission);
      return {
        ...prev,
        role_permissions: {
          ...prev.role_permissions,
          [role]: Array.from(new Set(next)),
        },
      };
    });
  };

  const savePlan = async () => {
    try {
      setSaveLoading(true);
      const payload = {
        code: form.code,
        name: form.name,
        description: form.description,
        monthly_price: Number(form.monthly_price || 0),
        yearly_price: Number(form.yearly_price || 0),
        is_active: Boolean(form.is_active),
        module_access: form.module_access,
        role_permissions: form.role_permissions,
      };
      if (selectedPlanId) {
        await workspaceAdminAPI.updateSubscriptionPlan(selectedPlanId, payload);
        toast.success('Plan updated');
      } else {
        await workspaceAdminAPI.createSubscriptionPlan(payload);
        toast.success('Plan created');
      }
      await loadData();
      resetForm();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save plan'));
    } finally {
      setSaveLoading(false);
    }
  };

  const deletePlan = async (plan) => {
    if (!window.confirm(`Delete/deactivate plan "${plan.name}"?`)) return;
    try {
      await workspaceAdminAPI.deleteSubscriptionPlan(plan.id);
      await loadData();
      if (String(selectedPlanId) === String(plan.id)) resetForm();
      toast.success('Plan deleted/deactivated');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete/deactivate plan'));
    }
  };

  const togglePlanStatus = async (plan) => {
    try {
      await workspaceAdminAPI.updateSubscriptionPlan(plan.id, { is_active: !plan.is_active });
      toast.success(`Plan marked as ${plan.is_active ? 'inactive' : 'active'}`);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update plan status'));
    }
  };

  const duplicatePlan = async (plan) => {
    try {
      const suffix = Date.now().toString().slice(-5);
      const duplicatePayload = {
        code: `${plan.code || 'plan'}_copy_${suffix}`.toLowerCase(),
        name: `${plan.name || 'Plan'} Copy`,
        description: plan.description || '',
        monthly_price: Number(plan.monthly_price || 0),
        yearly_price: Number(plan.yearly_price || 0),
        is_active: false,
        module_access: [...(plan.module_access || [])],
        role_permissions: { ...(plan.role_permissions || {}) },
      };
      await workspaceAdminAPI.createSubscriptionPlan(duplicatePayload);
      toast.success('Plan duplicated');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to duplicate plan'));
    }
  };

  const assignPlan = async () => {
    try {
      if (!assignment.tenantId || !assignment.planId) {
        toast.error('Tenant and plan are required');
        return;
      }
      setAssignLoading(true);
      await workspaceAdminAPI.assignTenantSubscription(Number(assignment.tenantId), {
        plan_id: Number(assignment.planId),
        status: 'active',
      });
      toast.success('Plan assigned to tenant');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign plan'));
    } finally {
      setAssignLoading(false);
    }
  };

  const runBootstrapSeed = async () => {
    if (!window.confirm('Run Bootstrap Default Plan + Seed now? This will auto-create missing subscriptions and seed role permission overrides.')) {
      return;
    }
    try {
      setBootstrapLoading(true);
      const response = await workspaceAdminAPI.bootstrapDefaultPlanAndSeed();
      const summary = response?.data || {};
      await loadData();
      toast.success(
        `Bootstrap completed: ${summary.active_plan_code || '-'} | Created ${summary.created_subscriptions || 0}, Updated ${summary.updated_subscriptions || 0}, Seeded ${summary.seeded_role_entries || 0}`
      );
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to run bootstrap seed'));
    } finally {
      setBootstrapLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid sm:grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => (
            <CardSkeleton key={i} rows={2} />
          ))}
        </div>
        <CardSkeleton rows={6} />
        <CardSkeleton rows={8} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Subscription Plans</h2>
          <p className="text-sm text-muted-foreground">Manage subscription plans, module access, and role permissions in a simpler superadmin workflow.</p>
        </div>
        <div className="flex items-center gap-2">
          {onCreatePlan && (
            <Button onClick={onCreatePlan}>
              <Plus className="h-4 w-4 mr-2" />
              Create Subscription Plan
            </Button>
          )}
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={runBootstrapSeed} disabled={bootstrapLoading}>
            <Wand2 className="h-4 w-4 mr-2" />
            {bootstrapLoading ? 'Running...' : 'Bootstrap Default Plan + Seed'}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <StatCard label="Total Plans" value={plans.length} icon={Layers} tone="primary" />
        <StatCard label="Active Plans" value={activePlansCount} icon={Check} tone="success" />
        <StatCard label="Tenants Assigned" value={assignedTenantsCount} icon={Users} tone="info" />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>Subscription Plan Blueprint</CardTitle>
              <CardDescription>Review how each plan packages tenant capacity, billing model, and role-level access before assigning it.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-muted-foreground">
                  <th className="text-left p-2">Plan</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Tenants</th>
                  <th className="text-left p-2">Child Tenants</th>
                  <th className="text-left p-2">Users</th>
                  <th className="text-left p-2">Employees</th>
                  <th className="text-left p-2">Storage</th>
                  <th className="text-left p-2">Trial</th>
                  <th className="text-left p-2">Default</th>
                  <th className="text-left p-2">Billing</th>
                  <th className="text-left p-2">Role Depth</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => {
                  const limits = plan.usage_limits || {};
                  const roleStats = rolePermissionCoverage[plan.id] || {};
                  return (
                    <tr key={`matrix-${plan.id}`} className="border-b transition-colors hover:bg-muted/40">
                      <td className="p-2 font-medium text-foreground">{plan.name}</td>
                      <td className="p-2">
                        <Badge variant={limits.is_free ? 'info' : 'soft'}>{limits.is_free ? 'Startup' : 'Paid'}</Badge>
                      </td>
                      <td className="p-2">{limits.tenants ?? '-'}</td>
                      <td className="p-2">{limits.sub_tenants ?? '-'}</td>
                      <td className="p-2">{limits.max_users ?? limits.users_per_tenant ?? '-'}</td>
                      <td className="p-2">{limits.max_employees ?? '-'}</td>
                      <td className="p-2">{limits.storage_limit_gb ? `${limits.storage_limit_gb} GB` : '-'}</td>
                      <td className="p-2">{limits.enable_trial === false ? 'Disabled' : limits.trial_days ? `${limits.trial_days} days` : 'Enabled'}</td>
                      <td className="p-2">{limits.is_default_plan ? 'Yes' : '-'}</td>
                      <td className="p-2 capitalize">{limits.billing_model ?? '-'}</td>
                      <td className="p-2 text-xs text-muted-foreground">
                        WA:{roleStats.workspace_admin || 0} | HRA:{roleStats.hr_admin || 0} | M:{roleStats.manager || 0} | HRM:{roleStats.hr_manager || 0} | E:{roleStats.employee || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {!onCreatePlan && (
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
              {selectedPlanId ? <Pencil className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
            </div>
            <div>
              <CardTitle>{selectedPlanId ? 'Edit Plan Configuration' : 'Create New Plan'}</CardTitle>
              <CardDescription>Define pricing, module access, and role-level permissions for this plan.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <Label>Code</Label>
              <Input value={form.code} onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))} />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
            </div>
            <div>
              <Label>Monthly Price ({selectedCurrency})</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={convertPlanPriceFromBase(form.monthly_price, selectedCurrency)}
                onChange={(e) => setForm((prev) => ({ ...prev, monthly_price: convertPlanPriceToBase(e.target.value, selectedCurrency) }))}
              />
            </div>
            <div>
              <Label>Yearly Price ({selectedCurrency})</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={convertPlanPriceFromBase(form.yearly_price, selectedCurrency)}
                onChange={(e) => setForm((prev) => ({ ...prev, yearly_price: convertPlanPriceToBase(e.target.value, selectedCurrency) }))}
              />
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="md:col-span-1">
              <Label>Status</Label>
              <Select
                value={form.is_active ? 'active' : 'inactive'}
                onValueChange={(value) => setForm((prev) => ({ ...prev, is_active: value === 'active' }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <Label>Currency</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLAN_CURRENCY_OPTIONS.map((currency) => (
                    <SelectItem key={currency} value={currency}>{currency}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
              Monthly preview: {formatPlanPriceFromBase(form.monthly_price, selectedCurrency)}
            </div>
            <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
              Yearly preview: {formatPlanPriceFromBase(form.yearly_price, selectedCurrency)}
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border rounded-md p-3">
              <p className="font-medium mb-2">Module Access</p>
              <div className="grid md:grid-cols-2 gap-2">
                {catalog.modules.map((moduleKey) => (
                  <label key={moduleKey} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.module_access.includes(moduleKey)}
                      onCheckedChange={(checked) => toggleModule(moduleKey, Boolean(checked))}
                    />
                    <span>{moduleKey}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="border rounded-md p-3">
              <p className="font-medium mb-2">Role Permissions</p>
              <div className="space-y-3 max-h-[260px] overflow-y-auto pr-1">
                {catalog.roles.map((role) => (
                  <div key={role}>
                    <p className="text-sm font-semibold mb-1">{role}</p>
                    <div className="grid grid-cols-2 gap-1">
                      {catalog.permissions.map((permission) => (
                        <label key={`${role}-${permission}`} className="flex items-center gap-2 text-xs">
                          <Checkbox
                            checked={Boolean(form.role_permissions?.[role]?.includes(permission))}
                            onCheckedChange={(checked) => togglePermission(role, permission, Boolean(checked))}
                          />
                          <span>{permission}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={savePlan} disabled={saveLoading}>
              {selectedPlanId ? <Check className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {saveLoading ? 'Saving...' : selectedPlanId ? 'Update Plan' : 'Create Plan'}
            </Button>
            {selectedPlanId && (
              <Button variant="outline" onClick={resetForm}>Cancel Edit</Button>
            )}
          </div>
        </CardContent>
      </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>Subscription Assignment</CardTitle>
              <CardDescription>Assign an active plan to a tenant workspace.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid md:grid-cols-4 gap-3">
          <div>
            <Label>Tenant</Label>
            <Select value={assignment.tenantId || '_none'} onValueChange={(v) => setAssignment((prev) => ({ ...prev, tenantId: v === '_none' ? '' : v }))}>
              <SelectTrigger><SelectValue placeholder="Select tenant" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {tenants.map((tenant) => (
                  <SelectItem key={tenant.id} value={String(tenant.id)}>
                    {tenant.name} ({tenantCurrentPlanById.get(String(tenant.id))})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Plan</Label>
            <Select value={assignment.planId || '_none'} onValueChange={(v) => setAssignment((prev) => ({ ...prev, planId: v === '_none' ? '' : v }))}>
              <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {plans.filter((plan) => plan.is_active).map((plan) => (
                  <SelectItem key={plan.id} value={String(plan.id)}>
                    {plan.name} ({plan.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button onClick={assignPlan} disabled={assignLoading}>
              {assignLoading ? 'Assigning...' : 'Assign Plan'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>Subscription Plans</CardTitle>
              <CardDescription>Search, filter, and manage pricing, limits, and role coverage for every plan.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search by plan name, code, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLAN_CURRENCY_OPTIONS.map((currency) => (
                    <SelectItem key={currency} value={currency}>{currency}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Pricing</TableHead>
                <TableHead>Limits</TableHead>
                <TableHead>Modules</TableHead>
                <TableHead>Role Coverage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {planTable.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <EmptyState
                      icon={Layers}
                      compact
                      title="No plans found"
                      description="No subscription plans match the current search or status filter."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                planTable.data.map((plan) => {
                  const roleCoverageCount = Object.values(plan.role_permissions || {}).filter((perms) => Array.isArray(perms) && perms.length > 0).length;
                  const addonTotals = getAiDashboardAddonTotals(plan.usage_limits || {});
                  const currentSubscriptionMonthlyTotal = Number(plan.monthly_price || 0) + Number(addonTotals.monthly_price || 0);
                  const currentSubscriptionYearlyTotal = Number(plan.yearly_price || 0) + Number(addonTotals.yearly_price || 0);
                  return (
                    <TableRow key={plan.id}>
                      <TableCell>
                        <div>
                          <p className="font-semibold text-foreground">{plan.name}</p>
                          <p className="text-xs text-muted-foreground">{plan.code}</p>
                          <p className="text-xs text-muted-foreground mt-1">{plan.description || 'No description'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm font-semibold text-primary">Current: {formatPlanPriceFromBase(currentSubscriptionMonthlyTotal, selectedCurrency)}/month</p>
                        <p className="text-xs text-muted-foreground">Yearly: {formatPlanPriceFromBase(currentSubscriptionYearlyTotal, selectedCurrency)}</p>
                        <p className="text-xs text-muted-foreground">Base: {formatPlanPriceFromBase(plan.monthly_price, selectedCurrency)} + AI: {formatPlanPriceFromBase(addonTotals.monthly_price, selectedCurrency)}</p>
                        {plan.usage_limits?.is_free ? <Badge variant="info" className="mt-2">Startup</Badge> : null}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs text-muted-foreground space-y-0.5">
                          <p>T: {plan.usage_limits?.tenants ?? '-'}</p>
                          <p>CT: {plan.usage_limits?.sub_tenants ?? '-'}</p>
                          <p>U: {plan.usage_limits?.max_users ?? plan.usage_limits?.users_per_tenant ?? '-'}</p>
                          <p>E: {plan.usage_limits?.max_employees ?? '-'}</p>
                          <p>S: {plan.usage_limits?.storage_limit_gb ? `${plan.usage_limits.storage_limit_gb} GB` : '-'}</p>
                          <p>Trial: {plan.usage_limits?.enable_trial === false ? 'off' : plan.usage_limits?.trial_days ? `${plan.usage_limits.trial_days}d` : 'on'}</p>
                          <p>Default: {plan.usage_limits?.is_default_plan ? 'yes' : 'no'}</p>
                          <p className="capitalize">B: {plan.usage_limits?.billing_model ?? '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell>{(plan.module_access || []).length}</TableCell>
                      <TableCell>{roleCoverageCount} / {catalog.roles.length}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant={plan.is_active ? 'default' : 'outline'}
                          onClick={() => togglePlanStatus(plan)}
                        >
                          {plan.is_active ? 'Active' : 'Inactive'}
                        </Button>
                      </TableCell>
                      <TableCell>{plan.updated_at ? new Date(plan.updated_at).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            title="Edit"
                            onClick={() => {
                              if (onEditPlan) {
                                onEditPlan(plan.id);
                                return;
                              }
                              setSelectedPlanId(plan.id);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" title="Duplicate Plan" onClick={() => duplicatePlan(plan)}>
                            Duplicate
                          </Button>
                          <Button size="icon" variant="outline" title="Delete/Deactivate" onClick={() => deletePlan(plan)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TableControls
            totalRows={planTable.totalRows}
            page={planTable.page}
            totalPages={planTable.totalPages}
            pageSize={planTable.pageSize}
            pageSizes={planTable.pageSizes}
            onPageSizeChange={planTable.onPageSizeChange}
            onPrevious={planTable.goToPreviousPage}
            onNext={planTable.goToNextPage}
            exportConfig={{
              title: 'Subscription Plans',
              fileName: `subscription-plans-${new Date().toISOString().split('T')[0]}`,
              rows: filteredPlans,
              columns: [
                { label: 'Plan Name', accessor: (row) => row.name || '' },
                { label: 'Code', accessor: (row) => row.code || '' },
                { label: 'Description', accessor: (row) => row.description || '' },
                { label: `Monthly Price (${selectedCurrency})`, accessor: (row) => formatPlanPriceFromBase(row.monthly_price, selectedCurrency) },
                { label: `Yearly Price (${selectedCurrency})`, accessor: (row) => formatPlanPriceFromBase(row.yearly_price, selectedCurrency) },
                { label: 'Plan Type', accessor: (row) => (row.usage_limits?.is_free ? 'startup' : 'paid') },
                { label: 'Max Users', accessor: (row) => row.usage_limits?.max_users ?? row.usage_limits?.users_per_tenant ?? '' },
                { label: 'Max Employees', accessor: (row) => row.usage_limits?.max_employees ?? '' },
                { label: 'Storage Limit (GB)', accessor: (row) => row.usage_limits?.storage_limit_gb ?? '' },
                { label: 'Enable Trial', accessor: (row) => (row.usage_limits?.enable_trial === false ? 'no' : 'yes') },
                { label: 'Trial Days', accessor: (row) => row.usage_limits?.trial_days ?? '' },
                { label: 'Default Plan', accessor: (row) => (row.usage_limits?.is_default_plan ? 'yes' : 'no') },
                { label: 'Modules Count', accessor: (row) => (row.module_access || []).length },
                { label: 'Status', accessor: (row) => (row.is_active ? 'active' : 'inactive') },
                { label: 'Updated At', accessor: (row) => row.updated_at || '' },
              ],
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionPlansRBAC;
